import React, { useState, useEffect, Fragment } from "react";
import { Listbox, Transition } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { useAuth } from "./context/AuthContext";
import api from "./services/api";

const PriceChange = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [updatedName, setUpdatedName] = useState("");
  const [updatedSortOrder, setUpdatedSortOrder] = useState("");
  
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [isAddingMode, setIsAddingMode] = useState(false);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newItemName, setNewItemName] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemSortOrder, setNewItemSortOrder] = useState("");

  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await api.get("/quotations/items/categories");
      setCategories(res.data || []);
    } catch (err) {
      console.error("Failed to load items", err);
      setMessage({ type: "error", text: "Failed to load items list." });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedItem(null);
    setNewPrice("");
    setUpdatedName("");
    setUpdatedSortOrder("");
    setMessage(null);
    setIsAddingMode(false);
    setIsAddingCategory(false);
  };

  const handleItemChange = (item) => {
    setSelectedItem(item);
    setNewPrice(item.price ? String(item.price) : "");
    setUpdatedName(item.name || "");
    setUpdatedSortOrder(item.sort_order !== undefined ? String(item.sort_order) : "0");
    setMessage(null);
    setIsAddingMode(false);
  };

  const handleUpdate = async () => {
    if (!selectedItem || !selectedItem.id || !newPrice || !updatedName) return;
    try {
      setUpdating(true);
      setMessage(null);
      await api.put(`/quotations/items/update-price/${selectedItem.id}`, { 
        price: parseFloat(newPrice),
        item_name: updatedName,
        sort_order: updatedSortOrder === "" ? 0 : parseInt(updatedSortOrder)
      });
      setMessage({ type: "success", text: "Item updated successfully!" });
      
      setCategories((prev) => {
        const newCats = prev.map((cat) => {
          if (cat.category !== selectedCategory.category) return cat;
          return {
            ...cat,
            items: cat.items.map((it) =>
              it.id === selectedItem.id ? { ...it, price: parseFloat(newPrice), name: updatedName, sort_order: updatedSortOrder === "" ? 0 : parseInt(updatedSortOrder) } : it
            ).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)), // sorting locally
          };
        });
        const updatedCat = newCats.find(c => c.category === selectedCategory.category);
        if (updatedCat) setSelectedCategory(updatedCat);
        return newCats;
      });
      
      setSelectedItem((prev) => ({ ...prev, price: parseFloat(newPrice), name: updatedName, sort_order: updatedSortOrder === "" ? 0 : parseInt(updatedSortOrder) }));
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to update item." });
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem || !selectedItem.id) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedItem.name}?`)) return;
    
    try {
      setDeleting(true);
      setMessage(null);
      await api.delete(`/quotations/items/delete/${selectedItem.id}`);
      setMessage({ type: "success", text: "Item deleted successfully!" });
      
      setCategories((prev) => {
        const newCats = prev.map((cat) => {
          if (cat.category !== selectedCategory.category) return cat;
          return {
            ...cat,
            items: cat.items.filter((it) => it.id !== selectedItem.id),
          };
        });
        const updatedCat = newCats.find(c => c.category === selectedCategory.category);
        if (updatedCat) setSelectedCategory(updatedCat);
        return newCats;
      });
      
      setSelectedItem(null);
      setNewPrice("");
      setUpdatedName("");
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to delete item." });
    } finally {
      setDeleting(false);
    }
  };

  const handleAdd = async () => {
    const categoryName = isAddingCategory ? newCategoryName : selectedCategory?.category;
    if (!categoryName || !newItemName || !newItemPrice) return;
    try {
      setAdding(true);
      setMessage(null);
      const res = await api.post(`/quotations/items/add`, {
        category: categoryName,
        item_name: newItemName,
        price: parseFloat(newItemPrice),
        sort_order: newItemSortOrder === "" ? 0 : parseInt(newItemSortOrder)
      });
      
      setMessage({ type: "success", text: "Item added successfully!" });
      
      const newItem = {
        id: res.data.id,
        name: newItemName,
        price: parseFloat(newItemPrice),
        sort_order: newItemSortOrder === "" ? 0 : parseInt(newItemSortOrder)
      };

      if (isAddingCategory) {
        setCategories((prev) => [...prev, { category: categoryName, items: [newItem] }]);
        setSelectedCategory({ category: categoryName, items: [newItem] });
        setIsAddingCategory(false);
      } else {
        setCategories((prev) => {
          const newCats = prev.map((cat) => {
            if (cat.category !== categoryName) return cat;
            return {
              ...cat,
              items: [...cat.items, newItem].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
            };
          });
          const updatedCat = newCats.find(c => c.category === categoryName);
          if (updatedCat) setSelectedCategory(updatedCat);
          return newCats;
        });
      }
      
      setSelectedItem(newItem);
      setNewPrice(String(newItem.price));
      setUpdatedName(newItemName);
      setUpdatedSortOrder(String(newItem.sort_order));
      setIsAddingMode(false);
      setNewItemName("");
      setNewItemPrice("");
      setNewItemSortOrder("");
      setNewCategoryName("");
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to add item." });
    } finally {
      setAdding(false);
    }
  };

  const rupee = (val) => Number(val).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-purple-200">Loading...</div>;
  if (user?.role !== "admin") return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-red-400">Access Denied. Admins only.</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white p-6 md:p-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
            Item Manager
          </h1>
          <p className="text-purple-200 mt-2">Update prices, Add categories/items, or Remove them</p>
        </div>

        <div className="glass p-8 rounded-3xl shadow-2xl border border-white/10 relative">
           <div className="absolute top-0 right-0 p-20 bg-purple-600/10 blur-3xl rounded-full pointer-events-none"></div>

          <div className="mb-8 relative z-10">
            <div className="flex justify-between items-end mb-2">
                <label className="block text-sm font-medium text-purple-200">Category</label>
                <button 
                    onClick={() => { setIsAddingCategory(!isAddingCategory); setIsAddingMode(false); setSelectedCategory(null); setSelectedItem(null); setMessage(null); }}
                    className="text-xs font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 bg-emerald-400/10 px-3 py-1.5 rounded-lg border border-emerald-400/20"
                >
                    {isAddingCategory ? "Cancel New Category" : <><PlusIcon className="w-4 h-4"/> Create Category</>}
                </button>
            </div>
            
            {!isAddingCategory ? (
                <Listbox value={selectedCategory} onChange={handleCategoryChange}>
                  <div className="relative mt-1">
                    <Listbox.Button className="relative w-full cursor-default rounded-xl bg-white/10 py-4 pl-4 pr-10 text-left border border-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/75 sm:text-sm shadow-inner transition-colors hover:bg-white/15">
                      <span className="block truncate text-lg">{selectedCategory ? selectedCategory.category : "Select a Category"}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-purple-300" aria-hidden="true" />
                      </span>
                    </Listbox.Button>
                    <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                      <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-slate-900/95 backdrop-blur-xl py-1 text-base shadow-2xl ring-1 ring-black/5 focus:outline-none sm:text-sm z-50 px-1 border border-white/10 scrollbar-thin">
                        {categories.map((cat, idx) => (
                          <Listbox.Option key={idx} className={({ active }) => `relative cursor-pointer select-none py-3 pl-4 pr-4 rounded-lg transition-colors ${active ? "bg-purple-600/30 text-purple-100" : "text-gray-300"}`} value={cat}>
                            {({ selected }) => <span className={`block truncate ${selected ? "font-bold text-white" : "font-normal"}`}>{cat.category}</span>}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
            ) : (
                <div className="bg-white/5 rounded-2xl p-6 border border-emerald-500/30 animate-fade-in-up mt-2 shadow-lg shadow-emerald-500/5">
                    <h3 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
                        <PlusIcon className="w-5 h-5"/>
                        Create New Category
                    </h3>
                    <div className="flex flex-col gap-4">
                        <div>
                            <label className="text-xs text-white/50 mb-1 block">New Category Name</label>
                            <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors" placeholder="e.g. New Pump Series" />
                        </div>
                        <div className="pt-4 border-t border-white/10">
                            <label className="text-xs text-white/50 mb-1 block">First Item Name</label>
                            <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors" placeholder="e.g. 1HP Model" />
                        </div>
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="text-xs text-white/50 mb-1 block">Price (Inc. 18% GST)</label>
                                <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 font-bold">₹</span>
                                    <input type="number" value={newItemPrice} onChange={e => setNewItemPrice(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl pl-8 pr-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors" placeholder="e.g. 12500" />
                                </div>
                            </div>
                            <div className="w-32">
                                <label className="text-xs text-white/50 mb-1 block">Sort Order</label>
                                <input type="number" value={newItemSortOrder} onChange={e => setNewItemSortOrder(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors text-center" placeholder="e.g. 1" />
                            </div>
                        </div>
                        <button onClick={handleAdd} disabled={adding || !newCategoryName || !newItemName || !newItemPrice} className="mt-2 w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/20 font-bold py-3.5 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 flex justify-center items-center gap-2">
                            {adding ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <CheckIcon className="w-5 h-5" />}
                            {adding ? "Creating..." : "Create Category & Item"}
                        </button>
                    </div>
                </div>
            )}
          </div>

          {!isAddingCategory && (
              <div className={`mb-8 transition-opacity duration-500 ${selectedCategory ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
                <div className="flex justify-between items-end mb-2">
                    <label className="block text-sm font-medium text-purple-200">Item</label>
                    {selectedCategory && (
                        <button 
                            onClick={() => { setIsAddingMode(!isAddingMode); setSelectedItem(null); setMessage(null); }}
                            className="text-xs font-bold text-emerald-400 hover:text-emerald-300 flex items-center gap-1 bg-emerald-400/10 px-3 py-1.5 rounded-lg border border-emerald-400/20"
                        >
                            {isAddingMode ? "Cancel Add" : <><PlusIcon className="w-4 h-4"/> Add New Item</>}
                        </button>
                    )}
                </div>
                
                {!isAddingMode ? (
                    <Listbox value={selectedItem} onChange={handleItemChange} disabled={!selectedCategory}>
                      <div className="relative mt-1">
                        <Listbox.Button className="relative w-full cursor-default rounded-xl bg-white/10 py-4 pl-4 pr-10 text-left border border-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/75 sm:text-sm shadow-inner transition-colors hover:bg-white/15">
                          <span className="block truncate text-lg">{selectedItem ? selectedItem.name : "Select an Item"}</span>
                          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                            <ChevronUpDownIcon className="h-5 w-5 text-purple-300" aria-hidden="true" />
                          </span>
                        </Listbox.Button>
                        <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
                          <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-xl bg-slate-900/95 backdrop-blur-xl py-1 text-base shadow-2xl ring-1 ring-black/5 focus:outline-none sm:text-sm z-50 px-1 border border-white/10 scrollbar-thin">
                            {selectedCategory?.items?.map((item, idx) => (
                              <Listbox.Option key={item.id || idx} className={({ active }) => `relative cursor-pointer select-none py-3 pl-4 pr-4 rounded-lg transition-colors ${active ? "bg-purple-600/30 text-purple-100" : "text-gray-300"}`} value={item}>
                                {({ selected }) => (
                                  <div className="flex justify-between">
                                     <span className={`truncate ${selected ? "font-bold text-white" : "font-normal"}`}>
                                        <span className="text-white/30 mr-2 text-xs w-4 inline-block">{item.sort_order || 0}</span>
                                        {item.name}
                                     </span>
                                     <span className="text-purple-300">₹{rupee(item.price)}</span>
                                  </div>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                ) : (
                    <div className="bg-white/5 rounded-2xl p-6 border border-emerald-500/30 animate-fade-in-up mt-4 shadow-lg shadow-emerald-500/5">
                        <h3 className="text-emerald-400 font-bold mb-4 flex items-center gap-2">
                            <PlusIcon className="w-5 h-5"/>
                            Add New Item to "{selectedCategory.category}"
                        </h3>
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="text-xs text-white/50 mb-1 block">Item Name</label>
                                <input type="text" value={newItemName} onChange={e => setNewItemName(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors" placeholder="e.g. 1HP 10 STAGE" />
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-1">
                                    <label className="text-xs text-white/50 mb-1 block">Price (Inc. 18% GST)</label>
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 font-bold">₹</span>
                                        <input type="number" value={newItemPrice} onChange={e => setNewItemPrice(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl pl-8 pr-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors" placeholder="e.g. 12500" />
                                    </div>
                                </div>
                                <div className="w-32">
                                    <label className="text-xs text-white/50 mb-1 block">Sort Order</label>
                                    <input type="number" value={newItemSortOrder} onChange={e => setNewItemSortOrder(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-colors text-center" placeholder="e.g. 1" />
                                </div>
                            </div>
                            <button onClick={handleAdd} disabled={adding || !newItemName || !newItemPrice} className="mt-2 w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg shadow-emerald-500/20 font-bold py-3.5 rounded-xl transition-all hover:scale-[1.01] disabled:opacity-50 disabled:hover:scale-100 flex justify-center items-center gap-2">
                                {adding ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <CheckIcon className="w-5 h-5" />}
                                {adding ? "Saving..." : "Save New Item"}
                            </button>
                        </div>
                    </div>
                )}
              </div>
          )}

          {selectedItem && !isAddingMode && !isAddingCategory && (
             <div className="bg-white/5 rounded-2xl p-6 border border-white/10 animate-fade-in-up mt-4 shadow-lg">
                
                <div className="flex flex-col gap-4">
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="text-sm font-medium text-white mb-2 block">Item Name</label>
                            <input type="text" value={updatedName} onChange={(e) => setUpdatedName(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors" placeholder="Edit item name" />
                        </div>
                        <div className="w-32">
                            <label className="text-sm font-medium text-white mb-2 block text-center">Sequence No</label>
                            <input type="number" value={updatedSortOrder} onChange={(e) => setUpdatedSortOrder(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors text-center" placeholder="0" />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-white mb-2 block">Item Price <span className="text-xs text-white/50 font-normal ml-1">(Inclusive of 18% GST)</span></label>
                        <div className="flex gap-4">
                            <div className="relative flex-1">
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30 font-bold">₹</span>
                                <input type="number" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} className="w-full bg-black/20 border border-white/20 rounded-xl pl-8 pr-4 py-3 text-xl font-bold text-white focus:outline-none focus:border-purple-500 transition-colors" placeholder="Enter new price" />
                            </div>
                            <button onClick={handleUpdate} disabled={updating || !newPrice || !updatedName} className={`px-8 py-3 rounded-xl font-bold text-lg shadow-lg flex items-center gap-2 transition-all ${updating || !newPrice || !updatedName ? 'bg-slate-700 text-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white hover:scale-105 shadow-emerald-500/20'}`}>
                               {updating ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <CheckIcon className="w-5 h-5" />}
                               Update
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mt-8 pt-6 border-t border-white/10 flex justify-between items-center">
                    <span className="text-xs text-white/30">ID: {selectedItem.id}</span>
                    <button onClick={handleDelete} disabled={deleting} className="text-red-400 hover:text-red-300 text-sm font-bold flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-red-500/10 transition-colors">
                        {deleting ? "Deleting..." : <><TrashIcon className="w-4 h-4"/> Remove Item</>}
                    </button>
                </div>
             </div>
          )}

          {message && (
              <div className={`mt-6 p-4 rounded-xl text-center font-bold animate-fade-in-up ${message.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-red-500/20 text-red-300 border border-red-500/30'}`}>
                  {message.type === 'success' ? '✅ ' : '❌ '}{message.text}
              </div>
          )}

        </div>
      </div>
      
      <style jsx>{`
        .glass { background: rgba(255, 255, 255, 0.05); backdrop-filter: blur(20px); }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in-up { animation: fadeInUp 0.4s ease-out forwards; }
        .scrollbar-thin { scrollbar-width: thin; }
        .scrollbar-thin::-webkit-scrollbar { height: 8px; width: 8px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.15); border-radius: 6px; }
      `}</style>
    </div>
  );
};

export default PriceChange;
